import { useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { comedoresApi } from '../../services/api'
import { nombreSede, PARENTESCOS, COMIDAS, fechaCorta } from '../comedores/comun'

// Familia y quién pide ayuda: el comedor del chico (con su tutor/a) y la familia en
// una sola lista, con lo que recibe cada integrante. En un chico es lo primero que
// hay que ver, porque la ayuda la piden los adultos a cargo.
//
// Si el chico está anotado sin tutor/a, se carga desde acá: el servidor ata el vínculo
// y los pone en el mismo hogar, así la familia se arma sola.

const iniciales = (m) => `${(m.apellido || '?')[0]}${(m.nombre || '')[0] || ''}`.toUpperCase()

function CargarTutor({ faltante, inscripcion, onListo }) {
  const [abierto, setAbierto] = useState(false)
  const [form, setForm] = useState({ dni: '', apellido: '', nombre: '', telefono: '', parentesco: faltante.parentesco || 'Madre' })
  const [encontrada, setEncontrada] = useState(null) // null sin buscar · persona · false no está
  const [guardando, setGuardando] = useState(false)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const buscar = async () => {
    const dni = form.dni.replace(/\D/g, '')
    if (dni.length < 6) { setEncontrada(null); return }
    try {
      const persona = (await comedoresApi.verificar({ dni })).data.data.persona
      setEncontrada(persona || false)
      if (persona) setForm((f) => ({ ...f, apellido: persona.apellido, nombre: persona.nombre, telefono: f.telefono || persona.telefono || '' }))
    } catch {
      setEncontrada(null)
    }
  }

  const guardar = async (e) => {
    e.preventDefault()
    const dni = form.dni.replace(/\D/g, '')
    if (dni.length < 6) return toast.error('Poné el DNI del tutor/a')
    if (!encontrada && !form.apellido.trim()) return toast.error('No está en el sistema: poné apellido y nombre')
    setGuardando(true)
    try {
      // Se reenvían los datos de la inscripción tal cual: el servidor los guarda completos.
      const i = inscripcion
      await comedoresApi.guardarBeneficiario({
        parentesco: form.parentesco, escuela: i.escuela, turno: i.turno, dias_asiste: i.dias_asiste,
        desayuno: i.desayuno, almuerzo: i.almuerzo, merienda: i.merienda,
        restriccion_medica: i.restriccion_medica, observaciones: i.observaciones,
        tutor_dni: dni, tutor_apellido: form.apellido.trim(), tutor_nombre: form.nombre.trim(), tutor_telefono: form.telefono.trim(),
      }, { id: faltante.beneficiario_id })
      toast.success('Tutor/a cargado: quedó vinculado y en el mismo hogar')
      setAbierto(false)
      onListo()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo cargar el tutor/a')
    } finally {
      setGuardando(false)
    }
  }

  if (!abierto) {
    return (
      <button type="button" onClick={() => setAbierto(true)}
        className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-amber-600">
        + Cargar tutor/a
      </button>
    )
  }

  return (
    <form onSubmit={guardar} className="mt-3 grid w-full gap-2 rounded-xl border border-amber-200 bg-white p-3 sm:grid-cols-6">
      <label className="block sm:col-span-2">
        <span className="mb-1 block text-[10px] font-bold uppercase text-ink-muted">DNI *</span>
        <input autoFocus className="input text-sm" inputMode="numeric" value={form.dni}
          onChange={(e) => { setEncontrada(null); set('dni')(e) }} onBlur={buscar} placeholder="30111222" />
      </label>
      <label className="block sm:col-span-2">
        <span className="mb-1 block text-[10px] font-bold uppercase text-ink-muted">Apellido</span>
        <input className="input text-sm" value={form.apellido} onChange={set('apellido')} disabled={!!encontrada} />
      </label>
      <label className="block sm:col-span-2">
        <span className="mb-1 block text-[10px] font-bold uppercase text-ink-muted">Nombre</span>
        <input className="input text-sm" value={form.nombre} onChange={set('nombre')} disabled={!!encontrada} />
      </label>
      <label className="block sm:col-span-3">
        <span className="mb-1 block text-[10px] font-bold uppercase text-ink-muted">Qué es del chico</span>
        <select className="input text-sm" value={form.parentesco} onChange={set('parentesco')}>
          {PARENTESCOS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <label className="block sm:col-span-3">
        <span className="mb-1 block text-[10px] font-bold uppercase text-ink-muted">Teléfono</span>
        <input className="input text-sm" inputMode="tel" value={form.telefono} onChange={set('telefono')} />
      </label>
      <p className="text-xs sm:col-span-4">
        {encontrada && <span className="font-semibold text-emerald-700">✓ Ya está en el sistema: se usa su ficha.</span>}
        {encontrada === false && <span className="font-semibold text-amber-700">No está en el sistema: se crea con estos datos.</span>}
      </p>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <button type="button" onClick={() => setAbierto(false)} className="rounded-lg px-3 py-2 text-sm font-bold text-ink-soft hover:bg-slate-100">Cancelar</button>
        <button type="submit" disabled={guardando} className="rounded-lg bg-social-700 px-3 py-2 text-sm font-bold text-white hover:bg-social-800 disabled:opacity-60">
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  )
}

function Comedor({ i, faltante, puedeCargar, onCambio }) {
  const comidas = COMIDAS.filter((c) => i[c.key]).map((c) => c.label).join(', ')
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-wide text-emerald-800">🍽️ Comedor comunitario</p>
          <Link to={`/comedores?sede=${i.comedor_id}`} className="text-lg font-black text-ink hover:text-social-700 hover:underline">
            {nombreSede(i.sede)}
          </Link>
          <p className="text-xs text-ink-soft">
            {[i.turno && `Turno ${i.turno.toLowerCase()}`, i.escuela, comidas, i.dias_asiste && `Días: ${i.dias_asiste}`].filter(Boolean).join(' · ') || 'Sin datos de turno ni escuela'}
          </p>
        </div>
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-800">
          {i.presentes_30} {i.presentes_30 === 1 ? 'vez' : 'veces'} en los últimos 30 días
        </span>
      </div>
      {i.restriccion_medica && <p className="mt-2 text-xs font-bold text-rose-700">⚕️ {i.restriccion_medica}</p>}

      <div className="mt-3 border-t border-emerald-200 pt-3">
        {i.tutor_persona_id ? (
          <p className="text-sm">
            <span className="font-bold text-ink-muted">Tutor/a: </span>
            <Link to={`/persona/${i.tutor_persona_id}`} className="font-bold text-ink hover:underline">{i.tutor_apellido}, {i.tutor_nombre}</Link>
            <span className="text-ink-soft">{i.parentesco ? ` · ${i.parentesco}` : ''}{i.tutor_dni ? ` · DNI ${i.tutor_dni}` : ''}{i.tutor_telefono ? ` · ${i.tutor_telefono}` : ''}</span>
          </p>
        ) : (
          <div className="rounded-xl bg-amber-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-black text-amber-900">Falta cargar quién está a cargo</p>
                <p className="text-xs text-amber-800">
                  La planilla dice {faltante?.parentesco ? `"${faltante.parentesco}"` : 'que tiene tutor/a'}, pero sin DNI: por eso no se ve su familia.
                </p>
              </div>
              {puedeCargar && faltante && <CargarTutor faltante={faltante} inscripcion={i} onListo={onCambio} />}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default function FamiliaYAyuda({ familia, puedeCargarTutor, onCambio, onIrAGrupoFamiliar, className = '' }) {
  if (!familia) return null
  const inscripciones = (familia.inscripciones_comedor || []).filter((i) => i.vigente)
  const faltantePor = new Map((familia.faltantes || []).map((f) => [f.beneficiario_id, f]))
  const miembros = familia.miembros || []

  return (
    <section className={`card p-6 ${className}`} aria-labelledby="familia-titulo">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="familia-titulo" className="text-sm font-bold uppercase tracking-wider text-violet-900">Familia y quién pide ayuda</h3>
          <p className="mt-1 text-xs text-ink-soft">Hogar, vínculos y tutor/a del comedor en una sola lista, con lo que recibe cada uno.</p>
        </div>
        {miembros.length > 0 && (
          <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-black text-violet-800">
            {miembros.length} {miembros.length === 1 ? 'persona' : 'personas'} · {familia.beneficios_familia} ayudas en la familia
          </span>
        )}
      </div>

      {inscripciones.length > 0 && (
        <div className="mb-4 space-y-3">
          {inscripciones.map((i) => (
            <Comedor key={i.beneficiario_id} i={i} faltante={faltantePor.get(i.beneficiario_id)} puedeCargar={puedeCargarTutor} onCambio={onCambio} />
          ))}
        </div>
      )}

      {miembros.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center">
          <p className="text-sm font-bold text-ink">No hay familia cargada</p>
          <p className="mt-1 text-xs text-ink-soft">
            {inscripciones.length ? 'Cargando al tutor/a del comedor se arma el hogar solo. ' : ''}
            También se puede armar a mano desde Grupo Familiar.
          </p>
          <button type="button" onClick={onIrAGrupoFamiliar} className="mt-3 text-xs font-bold text-violet-700 underline">Ir a Grupo Familiar</button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-100">
          {miembros.map((m) => (
            <li key={m.persona_id || `${m.apellido}-${m.nombre}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-black ${m.es_tutor ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-800'}`}>
                {iniciales(m)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-bold text-ink">
                  {m.persona_id
                    ? <Link to={`/persona/${m.persona_id}`} className="hover:text-violet-700 hover:underline">{m.apellido}, {m.nombre}</Link>
                    : `${m.apellido}, ${m.nombre}`}
                  <span className="ml-2 text-xs font-normal text-ink-muted">
                    {[m.dni && `DNI ${m.dni}`, m.edad != null && `${m.edad} años`, m.telefono].filter(Boolean).join(' · ')}
                  </span>
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {m.roles.map((r) => <span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-ink">{r}</span>)}
                  {m.convive && <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-bold text-violet-700">Convive</span>}
                  {m.comedor && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-800">🍽️ Comedor {nombreSede(m.comedor)}</span>}
                </div>
              </div>
              <div className="text-right text-xs">
                {m.beneficios
                  ? <><p className="font-black text-amber-700">{m.beneficios} {m.beneficios === 1 ? 'ayuda' : 'ayudas'}</p><p className="text-ink-muted">última {fechaCorta(m.ultimo_beneficio)}</p></>
                  : <p className="text-ink-muted">sin ayudas registradas</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
